<script>
    import debounce from "lodash/debounce";

    import { SEARCH } from "../../Utils/utilConstants.svelte";
    import { onMount } from "svelte";
    import { isFunctionEmpty } from "../../Utils/utilFunctions.svelte";
    import { Loader } from "./index.svelte";

    import { v4 } from "uuid";

    export let title = "";
    export let inputID = "";
    export let checkboxClass = "";
    export let inputClass = "";
    export let titleClass = "";
    export let isDisabled = false;
    export let onChangeFunc = () => {};
    export let onSearchFunc = () => {};
    export let optionsArr = [];
    export let searchResult = [];
    export let scene = "";
    export let customizedPlaceholder = "";
    export let showCollapsedPlaceholder = true;
    export let zIndex;
    export let showSelected = false;
    export let showOptionsWidthClass = "max-w-[328px]";

    $: if (searchResult.length) _syncSelectedOptions();

    $: fromScene = scene.toLowerCase();

    $: marginTop = fromScene == "calendar" ? "mt-[70px]" : "mt-[50px]";

    $: containerID = inputID + "Container";

    $: modifiedOptions = _modifyOptions(optionsArr);

    $: window.addEventListener("click", (e) => {
        if (
            (!document.getElementById(`${containerID}`) ||
                !document
                    .getElementById(`${containerID}`)
                    .contains(e.target)) &&
            e.target.id !== inputID
        )
            isOptionsListOpen = false;
    });

    $: inputPlaceholder = customizedPlaceholder
        ? customizedPlaceholder
        : showCollapsedPlaceholder
          ? `${searchResult.length ? `${searchResult.length == 1 ? (displayOptions.find(({ key }) => key == searchResult[0])?.value ?? "1 selected") : `${searchResult.length} selected`}` : ""}`
          : defaultPlaceholder;

    let isOptionsListOpen = false,
        defaultPlaceholder = "",
        displayOptions = [],
        isSearchingResults = false;

    let searchIndex = Number(0),
        showLoadingMoreIcon = false;

    const LOADINGMOREICON_ID = v4();

    const SEARCH_GAP = Number(25);

    const defaultCheckboxClass =
        "checkbox checkbox-secondary checkbox-xs cursor-pointer";
    const defaultInputClass = "input-sm w-48 bg-gray-200 cursor-default";
    const defaultTitleClass = "text-[11px] font-[500]";

    onMount(() => {
        if (!isFunctionEmpty(onSearchFunc)) {
            const inputField = document.getElementById(inputID);
            inputField.style.paddingRight = "30px";
        }
    });

    const toggleChange = (id) => {
        if (searchResult.includes(id))
            searchResult = searchResult.filter((result) => result != id);
        else searchResult.push(id);

        _updateSearchPlaceholder();

        debounceChangeFunc(id);
    };

    const debounceChangeFunc = debounce((lastChangeID) => {
        if(onChangeFunc)
            onChangeFunc(searchResult, { lastChangeID });
    }, SEARCH.DEBOUNCE_TIME);

    const debounceSearchFunc = debounce(
        async (e, index = 0, gap = SEARCH_GAP) => {
            let res = await onSearchFunc(e, index, gap);

            if (!res.length || res.length < gap) showLoadingMoreIcon = false;
            else {
                showLoadingMoreIcon = true;
                searchIndex = index + gap;

                const loadingMoreIcon =
                    document.getElementById(LOADINGMOREICON_ID);
                const ob = new IntersectionObserver(
                    async (entries) => {
                        for (const entry of entries) {
                            if (entry.isIntersecting)
                                await debounceSearchFunc(e, searchIndex, gap);
                        }
                    },
                    {
                        threshold: 0,
                    },
                );

                ob.observe(loadingMoreIcon);
            }

            if (!displayOptions.length) displayOptions = res;
            else displayOptions = [...displayOptions, ...res];

            isSearchingResults = false;
        },
        SEARCH.DEBOUNCE_TIME,
    );

    const handleSearch = async (e) => {
        searchIndex = Number(0);
        displayOptions = [];

        if (!isFunctionEmpty(onSearchFunc)) {
            isSearchingResults = true;
            await debounceSearchFunc(e);
        }

        filterOptions(e);
    };

    const removeSelectedResults = () => {
        searchResult = [];

        _updateSearchPlaceholder();

        const input = document.getElementById(`${inputID}`);

        if (input) input.value = "";

        debounceChangeFunc();
    };

    const filterOptions = (e) => {
        if (!e.target.value) displayOptions = [...modifiedOptions];
        else {
            var searchText = e.target.value.toLowerCase().trim();
            displayOptions = [
                ...modifiedOptions.filter(
                    (option) =>
                        option.key
                            .toString()
                            .toLowerCase()
                            .includes(searchText) ||
                        option.value
                            .toString()
                            .toLowerCase()
                            .includes(searchText),
                ),
            ];
        }
            
    };

    // HELPER FUNCTIONS
    const _modifyOptions = (arr = []) => {
        if (!arr.length) return [];

        const res = [];

        arr.map((optionObj) => {
            const entries = Object.entries(optionObj);

            res.push({
                key: entries[0][1],
                value: entries[1][1],
            });
        });

        displayOptions = [...res];

        return res;
    };

    const _updateSearchPlaceholder = () => {
        if (!searchResult.length) defaultPlaceholder = "";
        else defaultPlaceholder = searchResult.join(", ");
    };

    const _syncSelectedOptions = () => {
        _updateSearchPlaceholder();

        searchResult = searchResult.map((res) =>
            isNaN(res) ? res : Number(res),
        );
    };

    const getLabel = (id) => {
        if (!id || !displayOptions || !displayOptions.length) return undefined;
        const targetOption = displayOptions.find((op) => op.key === id);
        return targetOption ? targetOption.value : undefined;
    };

    const openList = () => {
        // delayed to bypass the window click event
        setTimeout(() => {
            isOptionsListOpen = true;
        }, 10);
    };
</script>

<div class="flex flex-col relative" style="z-index: {zIndex ?? '2'};">
    <p class={defaultTitleClass + " " + titleClass}>{title}</p>

    <div class="w-full relative">
        {#if showSelected && searchResult?.length && !isOptionsListOpen}
            <div
                class="border-b flex flex-row flex-wrap gap-2 {showOptionsWidthClass}"
            >
                {#each searchResult as selectedId}
                    <div
                        class="flex flex-row items-center px-2 mb-[2px] bg-[#F1F1F2] gap-x-[4px] rounded-[6px] max-w-[158px]"
                    >
                        <span
                            class="truncate text-left flex-1"
                            on:click|preventDefault={openList}
                        >
                            {getLabel(selectedId) ?? selectedId}
                        </span>
                        <button
                            class="btn btn-xs btn-ghost w-[15px] rounded-full p-0 m-0 border-none"
                            disabled={isDisabled}
                            on:click|preventDefault={(e) => {
                                e.stopPropagation();
                                toggleChange(selectedId);
                            }}
                        >
                            <i class="fa fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                {/each}
            </div>
        {:else}
            <input
                type="text"
                class={defaultInputClass + " " + inputClass}
                id={inputID}
                placeholder={inputPlaceholder}
                on:click|preventDefault={() => (isOptionsListOpen = true)}
                on:input={handleSearch}
                autocomplete="off"
            />
        {/if}
        {#if searchResult.length}
            <div
                class="absolute bottom-1.5 right-4 hover:cursor-pointer hover:text-error"
                on:click={removeSelectedResults}
            >
                X
            </div>
        {/if}
    </div>

    {#if isOptionsListOpen}
        <div
            class={`flex flex-col ${marginTop} max-h-[300px] gap-y-2 w-auto min-w-48 max-w-96 overflow-y-auto absolute border bg-white`}
            id={containerID}
        >
            <div
                class="flex flex-1 flex-col gap-y-2 p-4 w-full min-w-48 max-w-96"
            >
                {#if isSearchingResults}
                    <div class="flex flex-1 gap-x-4 w-48">
                        <Loader />
                        <p>Searching options</p>
                    </div>
                {:else if !isFunctionEmpty(onSearchFunc) && !displayOptions.length}
                    <p class="w-48">No options</p>
                {:else if displayOptions.length}
                    {#each displayOptions as option}
                        <div
                            class="flex items-center gap-2 min-h-[42px] h-auto mr-2"
                        >
                            <input
                                id={`${option.key}Option`}
                                type="checkbox"
                                class={defaultCheckboxClass +
                                    " " +
                                    checkboxClass}
                                checked={searchResult.includes(option.key)}
                                disabled={isDisabled}
                                on:change={() => toggleChange(option.key)}
                            />
                            <label
                                class="whitespace-nowrap cursor-pointer"
                                for={`${option.key}Option`}
                            >
                                {option.value}
                            </label>
                        </div>
                    {/each}
                {:else}
                    <p class="w-48">No options</p>
                {/if}

                <div
                    class={`flex flex-1 items-center justify-center ${showLoadingMoreIcon && !isSearchingResults ? "" : "hidden"}`}
                    id={LOADINGMOREICON_ID}
                >
                    <Loader />
                </div>
            </div>
        </div>
    {/if}
</div>

<style>
    p {
        color: #6f726c;
    }

    input[type="text"] {
        outline: 0;
        border-width: 0 0 2px;
        border-color: #818e9b;
        color: #270053;
    }

    input:focus {
        border-color: #270053;
    }

    label {
        min-width: 3rem;
    }

    .border-b {
        border-width: 0 0 2px;
        border-color: #818e9b;
    }
</style>
